import { type ColumnDef } from "_/components/table";

import { dateTimeString } from "_/utils/datetime";

/**
 * `JSON.stringify` replacer that stringifies `bigint` values instead of
 * throwing on them, as any DuckDB list over a 64-bit integer expression
 * arrives as an array of them.
 */
export function jsonReplacer(_key: string, value: unknown): unknown {
  return typeof value === "bigint" ? value.toString() : value;
}

/**
 * Render one DuckDB result value as text.
 *
 * Values arrive as Arrow JavaScript values: 64-bit integers are `bigint`,
 * timestamps are `Date`, and list columns such as `value_pair` are arrays.
 */
export function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return "NULL";
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value instanceof Date) {
    return dateTimeString({ value, precision: "millisecond" });
  }
  if (typeof value === "object") {
    // Throwing here would take down the whole results table, not just one cell.
    return JSON.stringify(value, jsonReplacer);
  }
  if (typeof value === "number") return value.toString();
  if (typeof value === "string") return value;

  // Every type a DuckDB row can hold is handled above.
  return "";
}

/**
 * Build table columns for a result set whose shape is only known at runtime.
 */
export function resultColumns(
  columns: string[]
): ColumnDef<Record<string, unknown>>[] {
  // `accessorFn` rather than `accessorKey`, because react-table reads a dot in
  // an accessor key as a path into a nested object, and a query is free to
  // alias a column to something with a dot in it.
  return columns.map((name) => ({
    id: name,
    header: name,
    accessorFn: (row) => row[name],
    cell: ({ row }) => formatCellValue(row.original[name]),
  }));
}
